import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { defaultStyle } from "components/Modals";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectGOActiveClients } from "store/selectors";
import { fetchGOActiveClients } from "store/appSlice";
import { transferGOCertificates } from "api/resources/go";
import dayjs from "dayjs";

interface TransferModalProps {
  certificates: any[];
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const TransferModal = ({
  certificates,
  onClose,
  onSuccess,
  onError,
}: TransferModalProps) => {
  const dispatch = useAppDispatch();
  const activeClients = useAppSelector(selectGOActiveClients);
  const [selectedReceiver, setSelectedReceiver] = useState<any>(null);
  const [formErrors, setFormErrors] = useState(null);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchGOActiveClients());
  }, [dispatch]);

  useEffect(() => {
    // Initialize quantities with certificate amounts
    const initialQuantities: { [key: number]: number } = {};
    certificates.forEach((cert) => {
      initialQuantities[cert.id] = cert.amount;
    });
    setQuantities(initialQuantities);
  }, [certificates]);

  const handleQuantityChange = (certId: number, value: string) => {
    const numValue = parseFloat(value);
    const cert = certificates.find((c) => c.id === certId);

    if (!cert) return;

    // Allow empty string for editing
    if (value === "") {
      setQuantities((prev) => ({ ...prev, [certId]: 0 }));
      return;
    }

    // Validate: must be a number and not exceed max amount
    if (!isNaN(numValue) && numValue >= 0 && numValue <= cert.amount) {
      setQuantities((prev) => ({ ...prev, [certId]: numValue }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validate all quantities are greater than 0
    const invalidQuantities = Object.entries(quantities).filter(
      ([id, qty]) => qty <= 0,
    );

    if (invalidQuantities.length > 0) {
      setFormErrors("All quantities must be greater than 0");
      return;
    }

    setLoading(true);
    setFormErrors(null);

    try {
      await transferGOCertificates({
        clientId: selectedReceiver.id,
        registryId: 99,
        certs: certificates.map((c) => ({
          certId: c.id,
          amount: quantities[c.id],
        })),
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error transferring certificates:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to transfer certificates";
      onError(errorMessage);
      setFormErrors(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const totalQuantity = Object.values(quantities).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  const totalAmount = certificates.reduce(
    (sum, cert) => sum + (cert.amount || 0),
    0,
  );

  return (
    <Box sx={{ ...defaultStyle, maxWidth: "700px", width: "100%" }}>
      <Typography variant="h5" component="h5" mb={2}>
        Transfer certificates
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Selected certificates ({certificates.length})
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            border: "1px solid #e0e0e0",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Transfer deadline</TableCell>
                <TableCell align="right">Available (MWh)</TableCell>
                <TableCell align="right">Quantity (MWh)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certificates.map((cert) => (
                <TableRow key={cert.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {cert.client.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cert.transferDeadline
                        ? dayjs(cert.transferDeadline).format("DD.MM.YYYY")
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{cert.amount}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={quantities[cert.id] || ""}
                      onChange={(e) =>
                        handleQuantityChange(cert.id, e.target.value)
                      }
                      onWheel={(e) =>
                        e.target instanceof HTMLElement && e.target.blur()
                      }
                      inputProps={{
                        min: 0,
                        max: cert.amount,
                        step: 0.01,
                        style: { textAlign: "right" },
                      }}
                      sx={{ width: 80 }}
                      error={quantities[cert.id] > cert.amount}
                      helperText={
                        quantities[cert.id] > cert.amount
                          ? "Exceeds available"
                          : ""
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Total
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {totalAmount.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {totalQuantity.toFixed(2)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Autocomplete
          options={activeClients || []}
          getOptionLabel={(option) => option.name || ""}
          value={selectedReceiver}
          onChange={(event, newValue) => setSelectedReceiver(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Receiver"
              required
              size="small"
              placeholder="Search clients..."
            />
          )}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          noOptionsText="No active clients available"
        />

        {formErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formErrors}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!selectedReceiver}
            loading={loading}
          >
            Transfer
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default TransferModal;
